import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import Accordion from '@mui/joy/Accordion'
import AccordionDetails from '@mui/joy/AccordionDetails'
import AccordionSummary from '@mui/joy/AccordionSummary'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import SvgIcon from '@mui/material/SvgIcon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

function ReservationCancellationIcon() {
    return (
        <SvgIcon viewBox="0 -960 960 960" sx={{ fontSize: 24, color: 'customGreen.dark' }}>
            <path d="m661-173 142-142q12-12 28-11.5t28 12.5q11 12 11 28t-11 28L690-88q-12 12-28 12t-28-12l-86-86q-11-11-11-28t11-28q11-11 28-11t28 11l57 57ZM360-344l-36 36q-11 11-28 11t-28-11q-11-11-11-28t11-28l36-36-36-36q-11-11-11-28t11-28q11-11 28-11t28 11l36 36 36-36q11-11 28-11t28 11q11 11 11 28t-11 28l-36 36 36 36q11 11 11 28t-11 28q-11 11-28 11t-28-11l-36-36ZM200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-40q0-17 11.5-28.5T280-880q17 0 28.5 11.5T320-840v40h320v-40q0-17 11.5-28.5T680-880q17 0 28.5 11.5T720-840v40h40q33 0 56.5 23.5T840-720v260q0 17-11.5 28T800-421q-17 0-28.5-11.5T760-461v-99H200v400h240q17 0 28.5 11.5T480-120q0 17-11.5 28.5T440-80H200Z" />
        </SvgIcon>
    )
}

type ReservationCancellationAlertProps = {
    mobile?: boolean
}

export default function ReservationCancellationAlert({
    mobile = false,
}: ReservationCancellationAlertProps) {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)
    const bulletKeys = [
        'checkout.cancellationAlert.bullet1',
        'checkout.cancellationAlert.bullet2',
        'checkout.cancellationAlert.bullet3',
    ]

    return (
        <Accordion
            expanded={expanded}
            onChange={(_event, nextExpanded) => {
                setExpanded(nextExpanded)
            }}
            sx={{
                border: '1px solid var(--joy-palette-neutral-100)',
                borderRadius: 10,
                backgroundColor: mobile ? 'white' : undefined,
                color: 'customBlack.main',
                overflow: 'hidden',
                '--ListItem-paddingY': '0px',
                '--ListItem-paddingX': '0px',
                ...(mobile ? {
                    boxShadow: `var(--joy-shadowRing, 0 0 #000),
                        0.3px 0.8px 1.1px rgba(187 187 187 / 0.11),
                        0.5px 1.3px 1.8px -0.6px rgba(187 187 187 / 0.18),
                        1.1px 2.7px 3.8px -1.2px rgba(187 187 187 / 0.26)`,
                } : {}),
            }}
        >
            <AccordionSummary
                indicator={(
                    <ExpandMoreRoundedIcon
                        sx={{
                            fontSize: 24,
                        }}
                    />
                )}
                slotProps={{
                    button: {
                        sx: {
                            px: 1.5,
                            py: mobile ? 1 : 1.5,
                            gap: 1.25,
                            '&& svg': {
                                fill: 'currentColor',
                            },
                            '&&:hover svg': {
                                fill: 'currentColor',
                            },
                            ...(mobile ? {
                                '&&:hover': {
                                    backgroundColor: 'white',
                                },
                            } : {}),
                            '&&:focus, &&:active:focus': {
                                boxShadow: 'none',
                            },
                        },
                    },
                    indicator: {
                        sx: {
                            transition: 'transform 0.2s ease',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        minWidth: 0,
                    }}
                >
                    <ReservationCancellationIcon />
                    <Typography
                        level={mobile ? 'body-xs' : 'title-sm'}
                        component="span"
                        sx={{
                            color: 'customGreen.dark',
                            fontWeight: 600,
                            textAlign: 'left',
                        }}
                    >
                        {t('checkout.cancellationAlert.heading')}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    '--AccordionDetails-transition':
                        'grid-template-rows 0.2s ease, padding-block 0.2s ease',
                }}
                slotProps={{
                    root: {
                        hidden: false,
                        'aria-hidden': !expanded,
                    },
                    content: {
                        sx: {
                            px: 1.5,
                            py: expanded ? 1.5 : 0,
                        },
                    },
                }}
            >
                <Box
                    component="ul"
                    sx={{
                        m: 0,
                        p: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        color: 'customGreen.dark',
                    }}
                >
                    {bulletKeys.map((key, index) => {
                        const isRestriction = index === bulletKeys.length - 1
                        const BulletIcon = isRestriction ? CloseRoundedIcon : CheckRoundedIcon

                        return (
                            <Box
                                key={key}
                                component="li"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.75,
                                    color: isRestriction ? 'danger.600' : 'customGreen.dark',
                                }}
                            >
                                <BulletIcon
                                    sx={{
                                        color: 'inherit',
                                        flexShrink: 0,
                                        fontSize: 16,
                                        mt: '1px',
                                    }}
                                />
                                <Typography
                                    component="span"
                                    level="body-xs"
                                    sx={{
                                        color: 'inherit',
                                    }}
                                >
                                    {t(key)}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
